import {Link} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/link.types';
import {useTranslation} from '@aboutyou/i18n';
import {useContactRoute} from '@aboutyou/router';
import {keyBy} from '@aboutyou/utils';
import {PathIntent} from '../../../../features/AppRedirect/helpers';
import {HelpCenterSectionFromIRIS, SectionsFromIRIS} from './iris';

/**
 * Function returning the url and translation keys to be used
 * to generate the Link at runtime. Both the label and linkTarget depend
 * on the current environment (locale, window.location) which cannot be
 * set at compile time
 *
 * This list is obtained from this document https://aboutyou.atlassian.net/wiki/spaces/AYSA/pages/986941680/WIP+Level+4+-+Self-Service+Flow
 */
export function useGenerateArticleCta(): (
  sectionId: number,
) => (Link & {appIntent?: PathIntent}) | undefined {
  const {t} = useTranslation();
  return (sectionId: number) => {
    switch (sectionId) {
      case 4498392997532:
      case 4805226233244:
      case 4588741328924:
      case 4498328796188:
      case 4498366371228:
      case 4588717965980:
      case 4588656334620: {
        return {
          label: t('My orders'),
          linkTarget: {url: '/a/orders', external: false},
          appIntent: 'a/orders',
        };
      }
      case 4805289594780:
      case 4588758287644: {
        return {
          label: t('Change personal data'),
          linkTarget: {url: '/a/profile', external: false},
        };
      }
      case 4588766753948: {
        return {
          label: t('Change password'),
          linkTarget: {url: '/a/profile', external: false},
          appIntent: 'a/password-reset',
        };
      }
      case 4588759500060: {
        return {
          label: t('My account'),
          linkTarget: {url: '/a/settings', external: false},
          appIntent: 'a/settings',
        };
      }
      case 5155547225244: {
        return {
          label: t('Add/edit your bank details'),
          linkTarget: {url: '/a/bank-data', external: false},
        };
      }
    }
  };
}

function buildBreadcrumbs(
  breadcrumbs: Link[],
  section: HelpCenterSectionFromIRIS,
  sectionsById: {[index: string]: HelpCenterSectionFromIRIS},
): Link[] {
  const nextParent = sectionsById[section.parent_section_id ?? 'null'];
  const newBreadcrumbs = [
    {
      label: section.name,
      linkTarget: {url: `/h/ssf/${section.id}`, external: false},
    },
    ...breadcrumbs,
  ];

  if (nextParent == null) {
    return newBreadcrumbs;
  } else {
    return buildBreadcrumbs(newBreadcrumbs, nextParent, sectionsById);
  }
}

export function useGetBreadcrumbsForSection(): (
  sections: SectionsFromIRIS,
  sectionId?: number,
) => Link[] {
  const {t} = useTranslation();

  const baseBreadcrumbs = [
    {
      label: t('Help'),
      linkTarget: {url: '/h', external: false},
    },
    {
      label: t('Choose topic'),
      linkTarget: {url: '/h/ssf/0', external: false},
    },
  ];

  return (sections: SectionsFromIRIS, sectionId?: number) => {
    if (sectionId == null) {
      return baseBreadcrumbs;
    }

    const sectionsById = keyBy(sections, 'id');
    const currentSection = sectionsById[sectionId]!;
    const breadcrumbs = buildBreadcrumbs([], currentSection, sectionsById);

    return [...baseBreadcrumbs, ...breadcrumbs];
  };
}

export function useGetContactUsCta() {
  const {t} = useTranslation();
  const contact = useContactRoute();

  return {
    label: t('Your topic is not included? <b>Contact us<b>'),
    linkTarget: {url: contact, external: false},
  };
}

export function useGetSectionLevelTitle() {
  const {t} = useTranslation();
  return ({
    isFirstLevel,
    isLastLevel,
  }: {
    isFirstLevel: boolean;
    isLastLevel: boolean;
  }) => {
    if (isFirstLevel) {
      return t('What topic do you need help with?');
    }
    if (isLastLevel) {
      return t('What is it about?');
    }
    return t('At which step do you have a problem?');
  };
}
